import { IGQLResponse } from './response';
import { IUserEdge, publicUserGQL, authenticatedUserGQL } from './user';
import { IMembershipEdge, authenticatedMembershipGQL } from './membership';
import { publicLOVGQL } from './lov';

/**
 * Creates an error IGQLResponse from the arguments
 * @param path
 * @param errorCode
 * @param statusCode
 * @param message
 * @returns IGQLResponse
 */
export const mockGQLErrorResponse = (
  path: string,
  errorCode: string,
  statusCode: number,
  message: string
): IGQLResponse => ({
  data: null,
  errors: [
    {
      message,
      path: [path],
      extensions: {
        error: errorCode,
        error_description: message,
        status: statusCode,
      },
    },
  ],
});

export const mockMeResponse = (key: string, me: IUserEdge): IGQLResponse => ({
  data: {
    [key]: me,
  },
});

export const mockMe: IUserEdge = {
  node: {
    id: 0,
    username: 'mock_username',
    mobile: '99991111',
    email: 'mock@email.com',
    lastName: 'mok',
    joinDate: new Date(),
    points: 999,
  },
};

/**
 * Interface for a GQL mock response matcher
 */
export interface IGQLMockRespMatcher {
  (variables: any): [number, IGQLResponse];
}

/**
 * Represents a GraphQL mock mapping
 */
export interface IGQLMockMapping {
  [key: string]: IGQLMockRespMatcher;
}

/**
 * Public GQL schema mock mapping
 */
export const mockPublicGQL: IGQLMockMapping = {
  [publicLOVGQL.allLOV]: () => [
    200,
    {
      data: {
        ...mockItemLOV,
        ...mockMembershipTypeLOV,
        ...mockMemberSourceLOV,
      },
    },
  ],
  [publicLOVGQL.itemLOV]: () => [
    200,
    {
      data: {
        ...mockItemLOV,
      },
    },
  ],
  [publicLOVGQL.membershipTypeLOV]: () => [
    200,
    {
      data: {
        ...mockMembershipTypeLOV,
      },
    },
  ],
  [publicLOVGQL.memberSourceLOV]: () => [
    200,
    {
      data: {
        ...mockMemberSourceLOV,
      },
    },
  ],
  // logIn
  [publicUserGQL.logIn]: (variables: {
    username: string;
    password: string;
  }) => {
    if (variables.password === 'password123') {
      return [
        200,
        {
          data: {
            logIn: {
              accessToken: 'some_token',
              tokenType: 'Bearer',
              expiresIn: 1200,
            },
          },
        },
      ];
    }

    return [
      200,
      {
        data: null,
        errors: [
          {
            message: 'Username or password is invalid',
            path: ['logIn'],
            extensions: {
              error: 'invalid_credentials',
              error_description: 'Username or password is invalid',
              status: 401,
            },
          },
        ],
      },
    ];
  },
};

export const mockMembershipActive: IMembershipEdge = {
  node: {
    id: 999,
    type: {
      id: 0,
      code: 'duration_1d',
      description: '1 Day Swapping',
    },
    externalTxnID: 'external_txn_id999',
    startDate: new Date('1999-10-25'),
    endDate: new Date('2099-10-25'),
    status: 'active',
    createdAt: new Date('2021-10-25'),
    updatedAt: new Date('2021-10-25'),
    stats: { inBalance: 99 },
  },
};

export const mockMembershipExpired: IMembershipEdge = {
  node: {
    id: 998,
    type: {
      id: 0,
      code: 'duration_60d',
      description: '2 Months Swapping',
    },
    externalTxnID: 'external_txn_id998',
    startDate: new Date('1999-10-25'),
    endDate: new Date('2005-10-25'),
    status: 'expired',
    createdAt: new Date('2021-10-25'),
    updatedAt: new Date('2021-10-25'),
    stats: { inBalance: 99 },
  },
};

export const mockItemLOV = {
  itemCategoryLOV: [
    {
      code: 'top',
      description: 'Top',
      children: [
        { code: 'top-vest', description: 'Vest' },
        { code: 'top-tee', description: 'T-shirts/Tees' },
      ],
    },
    {
      code: 'bottom',
      description: 'Bottom',
      children: [
        { code: 'bottom-pants_mini', description: 'Pants (Mini)' },
        { code: 'bottom-skirt_maxi', description: 'Skirt (Maxi)' },
      ],
    },
  ],
  itemSubCategoryLOV: [
    { code: 'top-vest', description: 'Vest' },
    { code: 'top-tee', description: 'T-shirts/Tees' },
    { code: 'bottom-pants_mini', description: 'Pants (Mini)' },
    { code: 'bottom-skirt_maxi', description: 'Skirt (Maxi)' },
  ],
  itemQualityLOV: [
    {
      code: 'used',
      description: 'Used',
    },
    {
      code: 'new',
      description: 'New',
    },
  ],
  itemStyleLOV: [
    {
      code: 'basic',
      description: 'Basic',
    },
    {
      code: 'formal',
      description: 'Formal',
    },
  ],
  itemBrandCategoryLOV: [
    {
      code: 'discount',
      description: 'Discount Area',
    },
    {
      code: 'value',
      description: 'Value Area',
    },
  ],
  itemSizeLOV: [
    {
      code: 'xs',
      description: 'XS',
    },
    {
      code: 's',
      description: 'S',
    },
    {
      code: 'fs',
      description: 'Free Size',
    },
  ],
};

export const mockMembershipTypeLOV = {
  membershipTypeLOV: [
    {
      code: 'duration_1d',
      description: '1 Day Swapping',
    },
    {
      code: 'duration_60d',
      description: '2 Months Swapping',
    },
    {
      code: 'duration_180d',
      description: '6 Months Swapping',
    },
    {
      code: 'duration_365d',
      description: '1 Year Swapping',
    },
  ],
};

export const mockMemberSourceLOV = {
  memberSourceLOV: [
    {
      code: 'referral',
      description: 'Referral',
    },
    {
      code: 'facebook',
      description: 'Facebook',
    },
    {
      code: 'instagram',
      description: 'Instagram',
    },
    {
      code: 'others',
      description: 'Others',
    },
  ],
};

/**
 * Authenticated GQL schema mock mapping
 */
export const mockAuthenticatedGQL: IGQLMockMapping = {
  [authenticatedUserGQL.me]: () => [200, mockMeResponse('me', mockMe)],
  [authenticatedUserGQL.updateMe]: () => [
    200,
    mockMeResponse('updateMe', mockMe),
  ],
  [authenticatedUserGQL.logOut]: () => [200, { data: { logOut: true } }],
  [authenticatedMembershipGQL.retrieveAllMemberships]: () => [
    200,
    {
      data: {
        retrieveAllMemberships: {
          edges: [mockMembershipActive, mockMembershipExpired],
        },
      },
    },
  ],
  [authenticatedMembershipGQL.retrieveActiveMembership]: () => [
    200,
    {
      data: {
        retrieveActiveMembership: mockMembershipActive,
      },
    },
  ],
  [authenticatedMembershipGQL.retrieveAllTxns]: () => [
    200,
    {
      data: {
        retrieveAllTxns: {
          edges: [
            {
              id: 999,
              memberID: 999,
              referenceNumber: 'TXN999',
              type: 'in',
              itemCount: 100,
              cents: 1000,
              points: 500,
              createdAt: new Date('2021-10-25'),
              updatedAt: new Date('2021-10-25'),
            },
          ],
        },
      },
    },
  ],
  [authenticatedMembershipGQL.retrieveSwapStats]: () => [
    200,
    {
      data: {
        retrieveSwapStats: {
          inItemCount: 100,
          outItemCount: 50,
          pointsEarned: 200,
          pointsUsed: 100,
        },
      },
    },
  ],
};
